import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { CurrencyCircleDollar, CaretRight } from '@phosphor-icons/react';
import api from '../utils/api';
import paymentsService from '../services/paymentsService';
import usePaymentPoller from '../hooks/usePaymentPoller';
import PaymentStatusModal from './PaymentStatusModal';
import './PaymentButton.css';

/**
 * PaymentButton Component (Enhanced with Polling)
 * 
 * Initiates online payment for a fee or subscription through Paystack
 * Opens payment window and continuously polls for status
 * Shows real-time payment status modal
 * 
 * Props:
 * - fee: The fee object (required, used for balance validation)
 * - onPaymentSuccess: Callback on successful payment
 * - disabled: Disable button
 * - fullWidth: Make button full width
 * - paymentConfig: Optional config for payment endpoint
 *   - endpoint: API endpoint to call (default: '/api/payments/initialize')
 *   - idField: Field name for resource ID (default: 'fee_id')
 *   - resourceId: The actual ID to send (default: fee.id)
 *   - pollingEndpoint: Endpoint pattern for polling status (default: '/api/payments/{id}')
 *     Use {id} as placeholder for transaction ID
 */
function PaymentButton({ 
  fee, 
  onPaymentSuccess = null, 
  disabled = false, 
  fullWidth = false,
  paymentConfig = null
}) {
  const [loading, setLoading] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [transactionId, setTransactionId] = useState(null);
  const [paymentWindow, setPaymentWindow] = useState(null);
  const hasStartedPollingRef = useRef(false);

  // Memoized callbacks - these don't change on every render
  const handlePaymentComplete = useCallback((response) => {
    toast.success('Payment completed successfully!');
    setShowStatusModal(false);
    if (onPaymentSuccess) {
      onPaymentSuccess(response);
    }
  }, [onPaymentSuccess]);

  // Handle failed payment
  const handlePaymentFailed = useCallback((response) => {
    toast.error(response.error || 'Payment verification failed');
  }, []);

  // Handle polling updates
  const handlePollingUpdate = useCallback((update) => {
    // Log for debugging, can be extended with real-time notifications
    console.log('Payment update:', update);
  }, []);

  // Initialize payment poller hook with memoized callbacks
  const {
    status,
    error,
    isPolling,
    attemptCount,
    startPolling,
    stopPolling,
    checkStatus,
  } = usePaymentPoller(transactionId, {
    onSuccess: handlePaymentComplete,
    onFailure: handlePaymentFailed,
    onPolling: handlePollingUpdate,
    pollingEndpoint: paymentConfig?.pollingEndpoint || '/api/payments/{id}',
    maxRetries: 5,
    initialInterval: 3000,
    maxInterval: 10000,
    totalTimeout: 120000, // 2 minutes - used as fallback only
    backoffMultiplier: 1.2,
  });

  // Verify payment immediately by calling backend endpoint
  const verifyPaymentNow = useCallback(async () => {
    if (!transactionId) return;
    
    try {
      console.log('Calling verify endpoint for:', transactionId);
      const verifyResponse = await paymentsService.verifyTransaction(transactionId);
      
      console.log('Verify endpoint response:', verifyResponse);
      
      if (verifyResponse.status === 'success' && verifyResponse.updated) {
        console.log('Payment verified and processed!');
        stopPolling();
        setShowStatusModal(false);
        handlePaymentComplete(verifyResponse);
      } else if (verifyResponse.status === 'failed') {
        console.log('Payment failed');
        stopPolling();
        handlePaymentFailed(verifyResponse);
      } else {
        console.log('Payment still processing, starting polling...');
        // If still processing, start polling
        if (!isPolling && !hasStartedPollingRef.current) {
          hasStartedPollingRef.current = true;
          startPolling();
        }
      }
    } catch (err) {
      console.error('Verification failed:', err);
      // Start polling as fallback
      if (!isPolling && !hasStartedPollingRef.current) {
        hasStartedPollingRef.current = true;
        startPolling();
      }
    }
  }, [transactionId, isPolling, stopPolling, handlePaymentComplete, handlePaymentFailed, startPolling]);

  // Check if payment window is still open - only verify after window closes
  useEffect(() => {
    if (!paymentWindow || !transactionId) return;

    const windowCheckInterval = setInterval(() => {
      if (paymentWindow.closed) {
        clearInterval(windowCheckInterval);
        console.log('Payment window closed, verifying payment with backend...');
        
        // Immediately verify payment when window closes (do NOT poll, use direct verification)
        verifyPaymentNow();
      }
    }, 500);

    return () => clearInterval(windowCheckInterval);
  }, [paymentWindow, transactionId, verifyPaymentNow]);

  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      if (paymentWindow && !paymentWindow.closed) {
        paymentWindow.close();
      }
      stopPolling();
    };
  }, [paymentWindow, stopPolling]);

  const handlePayment = useCallback(async () => {
    // Validate fee has outstanding balance
    if (!fee || fee.balance <= 0) {
      toast.error('No outstanding balance to pay');
      return;
    }

    setLoading(true);

    try {
      // Determine endpoint and payload structure
      const idField = paymentConfig?.idField || 'fee_id';
      const resourceId = paymentConfig?.resourceId || fee.id;

      // Build payload based on configuration
      const payload = { [idField]: resourceId };

      // Include custom amount if provided (for partial payments)
      if (fee.amount_to_pay && fee.amount_to_pay > 0) {
        payload.amount_to_pay = fee.amount_to_pay;
      }

      console.log(`🔵 [PAYMENT] Initiating payment to ${paymentConfig?.endpoint || '/api/payments/initialize'}`, payload);

      const response = paymentConfig?.endpoint
        ? await api.post(paymentConfig.endpoint, payload)
        : await paymentsService.initialize(payload);
      
      if (!response.success) {
        toast.error(response.error || 'Failed to initiate payment');
        setLoading(false);
        return;
      }

      // Get payment details
      const paymentUrl = response.payment_url;
      const txnId = response.transaction_id;
      const amount = response.amount;

      // Store transaction ID in session storage for verification
      sessionStorage.setItem('lastPaymentTransaction', txnId);
      sessionStorage.setItem('lastPaymentAmount', amount);

      // Set transaction ID for polling
      setTransactionId(txnId);

      // Open Paystack checkout in new window
      const newWindow = window.open(paymentUrl, 'paystack_payment', 'width=800,height=600');

      if (!newWindow) {
        toast.error('Please enable pop-ups to complete payment');
        setLoading(false);
        return;
      }

      // Store window reference
      setPaymentWindow(newWindow);

      // Show status modal immediately
      setShowStatusModal(true);

      // Note: startPolling() will be triggered by useEffect when transactionId is set

      setLoading(false);

    } catch (error) {
      console.error('Payment initiation error:', error);
      toast.error(error.message || 'Failed to initiate payment');
      setLoading(false);
    }
  }, [fee, paymentConfig]);

  const handleRetryPayment = useCallback(() => {
    if (paymentWindow && !paymentWindow.closed) {
      paymentWindow.close();
    }
    hasStartedPollingRef.current = false;
    setShowStatusModal(false);
    setTransactionId(null);
    setPaymentWindow(null);
    // Retry payment initiation
    handlePayment();
  }, [paymentWindow, handlePayment]);

  const handleManualStatusCheck = useCallback(async () => {
    await checkStatus();
  }, [checkStatus]);

  const handleCloseModal = useCallback(() => {
    if (paymentWindow && !paymentWindow.closed) {
      paymentWindow.close();
    }
    hasStartedPollingRef.current = false;
    stopPolling();
    setShowStatusModal(false);
    setTransactionId(null);
    setPaymentWindow(null);
  }, [paymentWindow, stopPolling]);

  return (
    <>
      <button
        className={`payment-btn ${fullWidth ? 'full-width' : ''}`}
        onClick={handlePayment}
        disabled={disabled || loading || !fee || fee.balance <= 0}
        title={!fee || fee.balance <= 0 ? 'No outstanding balance' : 'Pay via Paystack'}
      >
        <CurrencyCircleDollar size={16} weight="fill" />
        <span>{loading ? 'Opening Payment...' : 'Pay Now'}</span>
        {!loading && <CaretRight size={14} />}
      </button>

      {/* Payment Status Modal */}
      <PaymentStatusModal
        isOpen={showStatusModal}
        status={status}
        error={error}
        isPolling={isPolling}
        attemptCount={attemptCount}
        onClose={handleCloseModal}
        onRetry={handleRetryPayment}
        onManualCheck={handleManualStatusCheck}
        studentName={fee?.student_name || 'Student'}
        amount={fee?.amount_to_pay || fee?.balance}
        reference={status?.reference}
      />
    </>
  );
}

export default PaymentButton;
